import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Row, Col, Button, Spinner } from 'react-bootstrap';
import { useAbility } from '@casl/react';
import { useHistory } from 'react-router-dom';
import camelCaseRecursive from 'camelcase-keys-recursive';
import {
  indexCompanyAnnouncementsRequest,
  deleteCompanyAnnouncementRequest
} from '../../requests/companyAnnouncements';
import { DefaultModal, LinkBtn } from '../../components';
import { AbilityContext } from '../../config/abilityContext';
import './style.scss';
import { sendAlert } from '../../actions/utils';
import CompanyAnnouncementCard from '../Shared/CompanyAnnouncement/CompanyAnnouncementCard';
import CompanyAnnouncementModalShow from '../Shared/CompanyAnnouncement/CompanyAnnouncementModalShow';
import CompanyAnnouncementSidePanel from '../Shared/CompanyAnnouncement/CompanyAnnouncementSidePanel';

const CompanyAnnouncementIndex = () => {
  const dispatch = useDispatch();
  const ability = useAbility(AbilityContext);
  const history = useHistory();
  const [announcements, setAnnouncements] = useState([]);
  const [onRequest, setOnRequest] = useState(false);
  const [visibleCount, setVisibleCount] = useState(3);
  const [modalDeleteShow, setModalDeleteShow] = useState(false);
  const [modalDeleteItem, setModalDeleteItem] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [panelShow, setPanelShow] = useState(false);
  const [panelTitle, setPanelTitle] = useState('');
  const [panelContent, setPanelContent] = useState(null);

  const fetchAnnouncements = useCallback(() => {
    setOnRequest(true);
    indexCompanyAnnouncementsRequest({
      dispatch,
      successCallback: res => {
        const formatted = camelCaseRecursive(res.data.data);
        setAnnouncements(formatted);
      },
      callback: () => setOnRequest(false)
    });
  }, [dispatch]);

  const handleConfirmDelete = () => {
    if (!modalDeleteItem) return;
    deleteCompanyAnnouncementRequest(modalDeleteItem.id, {
      dispatch,
      successCallback: () => {
        setAnnouncements(prev => prev.filter(a => a.id !== modalDeleteItem.id));
        dispatch(sendAlert({ kind: 'success', message: 'Anuncio eliminado con éxito' }));
      },
      failureCallback: error => {
        dispatch(sendAlert({ kind: 'error', message: error.response?.data?.message || 'Error al eliminar' }));
      },
      callback: () => setModalDeleteShow(false)
    });
  };

  const handleLoadMore = () => {
    const nextCount = visibleCount + 3;
    setVisibleCount(Math.min(nextCount, announcements.length));
  };

  const handleShowInfo = announcement => {
    setPanelTitle('Detalle del Anuncio');
    setPanelContent(<CompanyAnnouncementModalShow announcement={announcement} />);
    setPanelShow(true);
  };

  const handleEdit = announcement => {
    history.push(`/company_announcements/${announcement.id}/edit`);
  };

  const renderContent = () => {
    if (onRequest) {
      return (
        <div className="text-center my-4">
          <Spinner animation="border" variant="primary" />
        </div>
      );
    }

    if (announcements.length > 0) {
      return (
        <>
          <Row>
            {announcements.slice(0, visibleCount).map(item => (
              <Col key={item.id} xs={12} md={4} className="mb-5">
                <CompanyAnnouncementCard
                  rrhh
                  location="default"
                  announcement={item}
                  showInfo={handleShowInfo}
                  showEdit={handleEdit}
                  onDelete={() => {
                    setModalTitle('Eliminar Anuncio');
                    setModalDeleteItem(item);
                    setModalDeleteShow(true);
                  }}
                />
              </Col>
            ))}
          </Row>

          {announcements.length > visibleCount && (
            <Row className="justify-content-end mt-3">
              <Col xs="auto">
                <Button onClick={handleLoadMore}>Ver más</Button>
              </Col>
            </Row>
          )}
        </>
      );
    }

    return <div className="text-center text-muted my-5">No hay anuncios disponibles.</div>;
  };

  useEffect(() => {
    fetchAnnouncements();
  }, [dispatch, fetchAnnouncements]);

  return (
    <>
      <Row className="mt-4 mb-4">
        <Col>
          <h2 className="mb-3 mt-3">Anuncios</h2>
        </Col>
        {ability.can('create', 'CompanyAnnouncement') && (
          <Col md={3} xl={2}>
            <LinkBtn variant="primary" className="mt-3" block to="/company_announcements/new">
              Nuevo
            </LinkBtn>
          </Col>
        )}
      </Row>
      <div className="yellow-wrapper">{renderContent()}</div>

      <CompanyAnnouncementSidePanel
        title={panelTitle}
        show={panelShow}
        onHide={() => {
          setPanelShow(false);
          setPanelContent(null);
        }}
      >
        {panelContent}
      </CompanyAnnouncementSidePanel>

      <DefaultModal
        title={modalTitle}
        body="¿Estás seguro que deseas eliminar este anuncio?"
        show={modalDeleteShow}
        handleClose={() => setModalDeleteShow(false)}
        handleConfirm={handleConfirmDelete}
        variantBtnClose="outline-info"
        variantBtnSave="danger"
      />
    </>
  );
};

export default CompanyAnnouncementIndex;
